import ConnectingIndicator from '@components/ConnectingIndicator/ConnectingIndicator';
import InfoIcon from '@servicetitan/anvil2/assets/icons/st/gnav_help_active.svg';
import { inboxLocators } from '@locators/inbox';
import { toastlocators } from '@locators/toast';
import TasksModule from '@modules/TasksModule';
import useTasks from '@modules/TasksModule/hooks/useTasks';
import useVoiceTask from '@modules/TasksModule/hooks/useVoiceTask';
import { Button, Icon, Tooltip } from '@servicetitan/anvil2';
import classNames from 'classnames';
import styles from './CallNotification.module.css';
import { TaskText } from '@components/Task/TaskText';
import { TransferTag } from '@components/Task/Tags';
import { taskStrings } from '@components/Task';
import useBrowserState from '@modules/BrowserModule/useBrowserState';
import { MicrophoneAccessType } from '@modules/BrowserModule/types';
import { FeatureFlagKeys, useFeatureFlags } from '@modules/FeatureFlagModule';
import { getTestIdProps } from '@lib/testUtils';

export const CallNotification = ({
  taskId,
  className,
}: {
  taskId: string;
  className?: string;
}) => {
  const { tasks } = useTasks();
  const { microphoneAccess } = useBrowserState();
  const {
    [FeatureFlagKeys.featureGracefullyHandleMicrophoneDisconnect]:
      hasGracefullyHandleMicrophoneDisconnect,
    [FeatureFlagKeys.featureVaEscalationSummary]: hasVaEscalationSummary,
  } = useFeatureFlags();

  const hasMicrophoneAccess =
    microphoneAccess === MicrophoneAccessType.ACCESS_GRANTED;
  const isAcceptDisabled =
    !!hasGracefullyHandleMicrophoneDisconnect && !hasMicrophoneAccess;
  const acceptTooltip = isAcceptDisabled ? taskStrings.noMicrophone : undefined;

  const task = tasks.find(
    (task) => task.attributes?.callId === taskId || task.id === taskId,
  );

  const { isIncomingTransfer, escalationSummary } = useVoiceTask(task);

  if (!task) {
    console.error(`Task with id ${taskId} not found for incoming call toast`);
    return null;
  }

  const { attributes, queueName } = task;
  const campaignName = task.attributes.campaign?.name;
  const workflowName = task.attributes.workflow?.name;

  const isCallConnecting = attributes?.isConnecting;

  const handleAcceptCall = () => {
    TasksModule.actionTask(task.id, 'accept');
  };

  const handleRejectCall = () => {
    TasksModule.actionTask(task.id, 'reject');
  };

  return (
    <div
      {...getTestIdProps(toastlocators.incomingCall.content)}
      className={classNames(styles.callNotification, className)}
      data-task-id={task.id}
    >
      {isIncomingTransfer && <TransferTag />}
      {!!hasVaEscalationSummary && escalationSummary && (
        <div
          {...getTestIdProps(toastlocators.incomingCall.escalationSummary)}
          className={styles.escalationSummary}
        >
          <TaskText
            label={`Summary:`}
            text={escalationSummary}
            tooltip={escalationSummary}
          />
        </div>
      )}
      <div className={styles.callInfo}>
        {workflowName && (
          <TaskText
            label={`Workflow:`}
            text={workflowName}
            tooltip={workflowName}
          />
        )}
        {queueName && (
          <TaskText label={`Queue:`} text={queueName} tooltip={queueName} />
        )}
        {campaignName && (
          <TaskText
            label={`Campaign:`}
            text={campaignName}
            tooltip={campaignName}
          />
        )}
      </div>

      {isCallConnecting ? (
        <ConnectingIndicator />
      ) : (
        <div className={styles.buttonsContainer}>
          <Button
            id={inboxLocators.taskList.acceptButton}
            appearance='primary'
            onClick={handleAcceptCall}
            disabled={isAcceptDisabled}
          >
            {taskStrings.acceptCall}
          </Button>
          <Button
            id={inboxLocators.taskList.rejectButton}
            onClick={handleRejectCall}
            appearance='danger-secondary'
          >
            {taskStrings.reject}
          </Button>

          {acceptTooltip && (
            <Tooltip>
              <Tooltip.Content>{acceptTooltip}</Tooltip.Content>
              <Tooltip.Trigger>
                <Icon svg={InfoIcon} size='medium' />
              </Tooltip.Trigger>
            </Tooltip>
          )}
        </div>
      )}
    </div>
  );
};
